import type { SxProps, Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import { uiPalette } from '@/theme/uiPalette'
import { LAYOUT_APP_BAR_HEIGHT_PX } from '@/components/layout/layoutConstants'

export const TOP_NAV_HEIGHT_PX = LAYOUT_APP_BAR_HEIGHT_PX

const KIOT_LIST_UNIFIED_SURFACE_BG = '#eceff3' as const
const KIOT_SIDEBAR_RAIL_BG = '#eceff3' as const

/** Mép ngang vùng nội dung chung (BasicLayout). */
const KIOT_PAGE_MARGIN_X_PX = 16 as const
/**
 * Inset ngang bên trong rail lọc — trùng `pl`/`pr` (spacing 1) trong `KiotFilterSidebarLayout`.
 * Header dùng `KIOT_PAGE_MARGIN_X_PX +` giá trị này để thẳng hàng với cột filter.
 */
const KIOT_FILTER_RAIL_INNER_INSET_X_PX = 8 as const
/** Độ rộng rail filter sidebar và cột đổi chi nhánh header. */
const KIOT_FILTER_SIDEBAR_WIDTH_PX = 250 as const

export const banhangKiotLike = {
  brandPrimary: uiPalette.primary.main,
  /** Thanh nav ngang — theo `--banhang-header-bg` (ThemeAccent). */
  kiotShellHeaderBg: 'var(--banhang-header-bg, #0070f4)',
  /**
   * Thanh POS (search + tab hóa đơn / header modal phiếu) — đậm hơn header một bậc.
   * Theo `--banhang-pos-chrome-bg` (= nav active / primary-dark).
   */
  kiotPosChromeBg: 'var(--banhang-pos-chrome-bg, #005ac3)',
  /** Đường kẻ ngăn menu ↔ thanh POS */
  kiotPosChromeTopBorder: '1px solid rgba(0, 0, 0, 0.18)',
  /** Tab/menu đang chọn — theo accent. */
  navItemActiveBg: 'var(--banhang-nav-active-bg, #005ac3)',
  navItemActiveBgHover: 'var(--banhang-nav-active-hover-bg, #004fa8)',
  /** Viền trắng nhạt cho item đang sáng / active trên header */
  navItemActiveBorder: '1px solid rgba(255, 255, 255, 0.35)',
  /** Khoảng cách giữa các nút cụm phải header */
  kiotHeaderRightItemGapPx: 5,
  /** Margin trên/dưới các nút cụm phải — header vẫn giữ chiều cao gốc */
  kiotHeaderRightItemMarginYPx: 4,
  /** Bo góc item nav (active & inactive đều 6px trên KiotViet) */
  navItemRadiusPx: 6,
  navItemActiveRadiusPx: 6,
  /** Panel menu con (flyout) — cùng tông tab active / accent. */
  kiotSubmenuPanelBg: 'var(--banhang-submenu-bg, #0054bb)',
  /** Bo góc khung menu con */
  kiotSubmenuPaperRadiusPx: 8,
  /** Bo góc từng dòng active/hover trong menu con */
  kiotSubmenuItemRadiusPx: 6,
  topNavBgHover: 'rgba(255,255,255,0.18)',
  topNavText: '#ffffff',
  topNavMuted: 'rgba(255,255,255,0.85)',
  /** `kv-btn-success` trên KiotViet (đo từ `rgb(0, 182, 62)`). */
  posButtonGreen: '#00b63e',
  posButtonGreenHover: '#019e36',
  /** Cùng tông success cho toolbar (giữ đồng bộ với POS / danh sách). */
  listToolbarGreen: '#00b63e',
  listToolbarGreenHover: '#019e36',
  kiotListUnifiedSurfaceBg: KIOT_LIST_UNIFIED_SURFACE_BG,
  kiotListToolbarStripBg: KIOT_LIST_UNIFIED_SURFACE_BG,
  pageCanvas: KIOT_LIST_UNIFIED_SURFACE_BG,
  /** Lề ngang vùng cuộn (BasicLayout); nền `AppBar` full bleed. */
  kiotPageContentMarginXPx: KIOT_PAGE_MARGIN_X_PX,
  /** Padding ngang thanh header xanh — bằng mép nội dung rail filter (`kiotPageContentMarginXPx` + inset rail). */
  kiotShellHeaderPaddingXPx: KIOT_PAGE_MARGIN_X_PX + KIOT_FILTER_RAIL_INNER_INSET_X_PX,
  tableBorder: '#e0e0e0',
  tableHeaderBg: '#fafafa',
  linkBlue: 'var(--banhang-primary, #0070f4)',
  filterWidth: KIOT_FILTER_SIDEBAR_WIDTH_PX,
  invoiceTableHeaderBg: `var(--banhang-invoice-thead-bg, ${uiPalette.table.headBg})`,
  invoiceSummaryRowBg: '#fff3e6',
  filterSidebarWidthPx: KIOT_FILTER_SIDEBAR_WIDTH_PX,
  /** Cột đổi chi nhánh trên header — bằng `filterSidebarWidthPx` (menu chính bắt đầu ngay sau mép phải cột này). */
  kiotHeaderBranchColumnWidthPx: KIOT_FILTER_SIDEBAR_WIDTH_PX,
  /** Khoảng cách trái menu chính sau ô chi nhánh — sát ô chọn. */
  kiotHeaderMainNavOffsetLeftPx: 0,
  /** Độ rộng hiển thị ô/cột chi nhánh trên header. */
  kiotHeaderBranchVisualWidthPx: KIOT_FILTER_SIDEBAR_WIDTH_PX - 60,
  filterSidebarRailBg: KIOT_SIDEBAR_RAIL_BG,
  filterSidebarTitleBarBg: KIOT_SIDEBAR_RAIL_BG,
  filterSidebarCardBg: '#ffffff',
  filterSidebarCardRadiusPx: 6,
  /** Viền nhẹ quanh mỗi khối lọc sidebar (card trắng trên nền xám). */
  filterSidebarSectionBorder: '1px solid rgba(0, 0, 0, 0.08)',
  /** KiotViet card lọc không có shadow → cạnh sắc nét nhờ border 1px solid. */
  filterSidebarCardShadow: 'none',
  filterSectionDividerColor: '#eeeeee',
  listSheetBg: KIOT_LIST_UNIFIED_SURFACE_BG,
  filterPanelBorder: '#e0e0e0',
  filterPanelBorderRadiusPx: 2,
  filterSectionSummaryBg: '#ffffff',
  /** Font giống KiotViet (Inter load ở public/index.html). Arial không có nét 600 → bị vẽ thành bold. */
  kiotFontStack: 'Inter, Roboto, Helvetica, Arial, sans-serif',
  kiotFontSizePx: 13,
  /** Font size cho thanh menu chính và tên chi nhánh trên header. */
  kiotNavFontSizePx: 14,
  /** Cỡ icon module trên thanh nav xanh — outlined nhỏ gọn. */
  kiotNavIconSizePx: 18,
  /** Font weight tab header / submenu — chữ đậm. */
  kiotNavFontWeight: 600,
  kiotPageTitleFontSizePx: 18,
  kiotLineHeight: 1.4,
  kiotSearchBarHeightPx: 34,
  kiotSearchBarBg: '#ffffff',
  kiotSearchBarBorder: '#d0d0d0',
  kiotSearchBarBorderHover: '#b0b0b0',
  kiotToolbarSearchMaxWidthPx: 400,
  kiotToolbarSearchMinWidthPx: 200,
  /** Text Kiot sale: gần `#15171a` — đậm, không dùng xám nhạt cho chữ chính. */
  kiotTextPrimary: '#15171a',
  kiotTextSecondary: '#3d4654',
  kiotTextMuted: '#6b7280',
  /** Popup form thêm/sửa hàng hóa — nền trắng (KiotViet Products). */
  kiotFormSurfaceBg: '#ffffff',
  kiotFormLabelFontSizePx: 13,
  kiotFormLabelFontWeight: 500,
  /** Đậm, tương phản cao — tránh `text.secondary` MUI (#666). */
  kiotFormLabelColor: '#222222',
  kiotFormInputFontSizePx: 14,
  kiotFormInputFontWeight: 400,
  kiotFormInputColor: '#222222',
  kiotFormPlaceholderColor: '#999999',
  kiotFormInputBg: '#ffffff',
  kiotFormInputBgDisabled: '#f5f5f5',
  kiotFormInputBorder: '#dcdcdc',
  kiotFormInputBorderHover: '#c8c8c8',
  kiotFormInputBorderFocus: '#0070d2',
  kiotFormInputFocusShadow: '0 0 0 2px rgba(0, 112, 210, 0.18)',
  kiotFormInputHeightPx: 36,
  kiotFormInputRadiusPx: 8,
  kiotFormInputPaddingX: 12,
  kiotFormLabelGapPx: 0,
  /** Viền popover / menu — đồng bộ khối bảng list. */
  kiotTableOuterBorder: 'var(--banhang-table-wrapper-border, #eaedf1)',
  kiotControlRadiusPx: 2,
  kiotButtonHeightPx: 30,
  kiotListCardShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
} as const

/** Padding tab header — KiotViet `.kv-nav-link`: `7px 15px`; X hơi hẹp hơn để gọn thanh menu. */
export const KIOT_HEADER_NAV_PADDING_Y = 0.875
export const KIOT_HEADER_NAV_PADDING_X = 1.5

export type KiotHeaderNavHighlightColors = {
  activeBg: string
  activeBgHover: string
}

export const getKiotHeaderNavHighlightSurfaceSx = (
  highlighted: boolean,
  colors: KiotHeaderNavHighlightColors,
): { bgcolor: string; border: string; boxSizing: 'border-box' } =>
  highlighted
    ? {
        bgcolor: colors.activeBg,
        border: banhangKiotLike.navItemActiveBorder,
        boxSizing: 'border-box',
      }
    : {
        bgcolor: 'transparent',
        border: '1px solid transparent',
        boxSizing: 'border-box',
      }

export const getKiotHeaderNavHoverBackground = (
  highlighted: boolean,
  colors: KiotHeaderNavHighlightColors,
): string => (highlighted ? colors.activeBgHover : banhangKiotLike.topNavBgHover)

export const buildKiotHeaderNavButtonSx = (
  highlighted: boolean,
  colors: KiotHeaderNavHighlightColors,
): SxProps<Theme> => {
  const radiusPx = highlighted
    ? banhangKiotLike.navItemActiveRadiusPx
    : banhangKiotLike.navItemRadiusPx
  const radius = `${radiusPx}px`
  const surface = getKiotHeaderNavHighlightSurfaceSx(highlighted, colors)
  return {
    flexShrink: 0,
    alignSelf: 'stretch',
    height: '100%',
    minHeight: TOP_NAV_HEIGHT_PX,
    textTransform: 'none',
    fontFamily: banhangKiotLike.kiotFontStack,
    fontWeight: banhangKiotLike.kiotNavFontWeight,
    fontSize: `${banhangKiotLike.kiotNavFontSizePx}px`,
    letterSpacing: '0.01em',
    lineHeight: banhangKiotLike.kiotLineHeight,
    color: banhangKiotLike.topNavText,
    borderRadius: radius,
    px: KIOT_HEADER_NAV_PADDING_X,
    py: KIOT_HEADER_NAV_PADDING_Y,
    minWidth: 'auto',
    boxShadow: 'none',
    transform: 'none',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    ...surface,
    '&:hover': {
      border: surface.border,
      boxShadow: 'none',
      transform: 'none',
      bgcolor: getKiotHeaderNavHoverBackground(highlighted, colors),
      borderRadius: radius,
    },
    '&:focus': { outline: 'none', boxShadow: 'none' },
    '&:focus-visible': { outline: 'none', boxShadow: 'none' },
    '&:active': {
      border: surface.border,
      boxShadow: 'none',
      transform: 'none',
    },
  }
}

/** Icon toolbar header — luôn sáng (active), full chiều cao bar. */
export const buildKiotHeaderIconButtonSx = (
  colors: KiotHeaderNavHighlightColors,
): SxProps<Theme> => {
  const radius = `${banhangKiotLike.navItemActiveRadiusPx}px`
  const surface = getKiotHeaderNavHighlightSurfaceSx(true, colors)
  return {
    color: banhangKiotLike.topNavText,
    alignSelf: 'stretch',
    height: '100%',
    width: 40,
    minWidth: 40,
    borderRadius: radius,
    py: 0,
    boxShadow: 'none',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    ...surface,
    '&:hover': {
      bgcolor: getKiotHeaderNavHoverBackground(true, colors),
      border: surface.border,
      borderRadius: radius,
    },
  }
}

const listToolbarButtonHoverMotionSx: SxProps<Theme> = {
  transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
}

export const kiotListToolbarContainedButtonSx: SxProps<Theme> = {
  ...listToolbarButtonHoverMotionSx,
  textTransform: 'none',
  fontSize: `${banhangKiotLike.kiotFontSizePx}px`,
  fontFamily: banhangKiotLike.kiotFontStack,
  fontWeight: 600,
  minHeight: banhangKiotLike.kiotSearchBarHeightPx,
  height: banhangKiotLike.kiotSearchBarHeightPx,
  px: 1.5,
  py: 0,
  lineHeight: 1.25,
  boxShadow: 'none',
  boxSizing: 'border-box',
  borderRadius: `${banhangKiotLike.kiotFormInputRadiusPx}px`,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  '&:hover': {
    bgcolor: 'primary.dark',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: 'action.disabledBackground',
    color: 'action.disabled',
  },
}

/** Nút "Tạo mới" kiểu Kiot: nền trắng + viền theo `primary` (chủ đề). */
export function getKiotListToolbarPrimaryOutlineButtonSx(theme: Theme): SxProps<Theme> {
  const main = theme.palette.primary.main
  const dark = theme.palette.primary.dark
  return {
    ...listToolbarButtonHoverMotionSx,
    textTransform: 'none',
    fontSize: `${banhangKiotLike.kiotFontSizePx}px`,
    fontFamily: banhangKiotLike.kiotFontStack,
    fontWeight: 600,
    minHeight: banhangKiotLike.kiotSearchBarHeightPx,
    height: banhangKiotLike.kiotSearchBarHeightPx,
    px: 1.5,
    py: 0,
    lineHeight: 1.25,
    boxSizing: 'border-box',
    borderRadius: `${banhangKiotLike.kiotFormInputRadiusPx}px`,
    border: `1px solid ${main}`,
    bgcolor: '#ffffff',
    color: main,
    '&:hover': {
      borderColor: dark,
      bgcolor: alpha(main, 0.12),
    },
  }
}

/** Nút xóa hàng loạt trên toolbar: viền + chữ đỏ (error). */
export function getKiotListToolbarErrorOutlineButtonSx(theme: Theme): SxProps<Theme> {
  const main = theme.palette.error.main
  const dark = theme.palette.error.dark
  return {
    ...listToolbarButtonHoverMotionSx,
    textTransform: 'none',
    fontSize: `${banhangKiotLike.kiotFontSizePx}px`,
    fontFamily: banhangKiotLike.kiotFontStack,
    fontWeight: 600,
    minHeight: banhangKiotLike.kiotSearchBarHeightPx,
    height: banhangKiotLike.kiotSearchBarHeightPx,
    px: 1.5,
    py: 0,
    lineHeight: 1.25,
    boxSizing: 'border-box',
    borderRadius: `${banhangKiotLike.kiotFormInputRadiusPx}px`,
    border: `1px solid ${main}`,
    bgcolor: '#ffffff',
    color: main,
    '& .MuiButton-startIcon': { color: main },
    '&:hover': {
      borderColor: dark,
      bgcolor: alpha(main, 0.08),
      color: dark,
      '& .MuiButton-startIcon': { color: dark },
    },
  }
}

/** Khoảng icon `startIcon` — đồng bộ Thêm / Nhập / Lọc. */
export const kiotToolbarButtonStartIconSx: SxProps<Theme> = {
  '& .MuiButton-startIcon': { mr: 0.75, ml: -0.25 },
}

/** Layout chung nút filter (màu lấy từ `color="primary"` trên Button). */
export const kiotFilterButtonLayoutSx: SxProps<Theme> = {
  textTransform: 'none',
  fontSize: `${banhangKiotLike.kiotFontSizePx}px`,
  fontFamily: banhangKiotLike.kiotFontStack,
  fontWeight: 600,
  minHeight: banhangKiotLike.kiotSearchBarHeightPx,
  height: banhangKiotLike.kiotSearchBarHeightPx,
  py: 0,
  lineHeight: 1.25,
  boxShadow: 'none',
  boxSizing: 'border-box',
  borderRadius: `${banhangKiotLike.kiotFormInputRadiusPx}px`,
  ...kiotToolbarButtonStartIconSx,
}

/** Nút «Đặt lại» — layout; màu/viền xám override ở `filterClearButtonSx`. */
export const kiotFilterClearButtonSx: SxProps<Theme> = {
  ...kiotFilterButtonLayoutSx,
  px: 1.5,
}

/** Nút «Tìm kiếm» / «Lọc» — layout; màu `primary` contained trên component. */
export const kiotFilterSearchButtonSx: SxProps<Theme> = {
  ...kiotFilterButtonLayoutSx,
  px: 2,
}

/** Nút "Import/Xuất" kiểu Kiot: nền trắng + viền xám. */
export const kiotListToolbarNeutralOutlineButtonSx: SxProps<Theme> = {
  ...listToolbarButtonHoverMotionSx,
  textTransform: 'none',
  fontSize: `${banhangKiotLike.kiotFontSizePx}px`,
  fontFamily: banhangKiotLike.kiotFontStack,
  fontWeight: 600,
  minHeight: banhangKiotLike.kiotSearchBarHeightPx,
  height: banhangKiotLike.kiotSearchBarHeightPx,
  px: 1.5,
  py: 0,
  lineHeight: 1.25,
  boxSizing: 'border-box',
  borderRadius: '8px',
  border: '1px solid #cfd5dc',
  bgcolor: '#ffffff',
  color: '#3e464f',
  '&:hover': {
    borderColor: '#a8b3bc',
    bgcolor: '#e8edf2',
  },
}
